import { z } from "zod";
import { paginationSchema } from "./common.js";
import { certificationTypeSchema } from "./certifications.js";

export const equipmentTypeSchema = z.enum([
  "HEAVY_EQUIPMENT",
  "VEHICLE",
  "TOOL",
  "RENTAL",
  "OTHER",
]);

export const equipmentStatusSchema = z.enum([
  "AVAILABLE",
  "IN_USE",
  "MAINTENANCE",
  "OUT_OF_SERVICE",
]);

export const createEquipmentSchema = z.object({
  equipmentId: z
    .string()
    .trim()
    .min(1, "Equipment ID is required")
    .max(64)
    .regex(/^[A-Za-z0-9-_]+$/, "ID may only contain letters, numbers, hyphens, and underscores"),
  name: z.string().trim().min(1, "Name is required").max(200),
  type: equipmentTypeSchema.default("OTHER"),
  hourlyCost: z.coerce.number().min(0).max(99999).optional().nullable(),
  siteId: z.string().uuid().optional().nullable(),
  requiredCertification: certificationTypeSchema.optional().nullable(),
  status: equipmentStatusSchema.default("AVAILABLE"),
  notes: z.string().trim().max(5000).optional().nullable(),
});

export const updateEquipmentSchema = createEquipmentSchema.partial();

export const equipmentStatusUpdateSchema = z.object({
  status: equipmentStatusSchema,
  notes: z.string().trim().max(5000).optional().nullable(),
});

export const equipmentListQuerySchema = paginationSchema.extend({
  siteId: z.string().uuid().optional(),
  type: equipmentTypeSchema.optional(),
  status: equipmentStatusSchema.optional(),
  q: z.string().trim().optional(),
});

export const equipmentSummarySchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  equipmentId: z.string(),
  name: z.string(),
  type: equipmentTypeSchema,
  hourlyCost: z.number().nullable(),
  siteId: z.string().uuid().nullable(),
  siteName: z.string().nullable(),
  siteCode: z.string().nullable(),
  requiredCertification: certificationTypeSchema.nullable(),
  status: equipmentStatusSchema,
  notes: z.string().nullable(),
  periodUsageHours: z.number(),
  estimatedPeriodCost: z.number().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const equipmentUsageEventSchema = z.object({
  id: z.string().uuid(),
  date: z.string(),
  usageHours: z.number(),
  workerName: z.string().nullable(),
  siteName: z.string().nullable(),
  notes: z.string().nullable(),
});

export const equipmentWorkerUsageSchema = z.object({
  workerId: z.string().uuid(),
  workerName: z.string(),
  totalHours: z.number(),
});

export const equipmentAuditSchema = z.object({
  id: z.string().uuid(),
  action: z.string(),
  actorName: z.string().nullable(),
  createdAt: z.string(),
  summary: z.string().nullable(),
});

export const equipmentDetailSchema = equipmentSummarySchema.extend({
  canEdit: z.boolean(),
  canViewCosts: z.boolean(),
  payPeriod: z
    .object({
      id: z.string().uuid(),
      label: z.string(),
    })
    .nullable(),
  usageTimeline: z.array(equipmentUsageEventSchema),
  workersWhoUsed: z.array(equipmentWorkerUsageSchema),
  auditActivity: z.array(equipmentAuditSchema),
});

export const equipmentFormOptionsSchema = z.object({
  sites: z.array(z.object({ id: z.string().uuid(), name: z.string(), code: z.string() })),
});

export const equipmentImportRowSchema = z.object({
  equipmentId: z.string().trim().min(1),
  name: z.string().trim().min(1),
  type: equipmentTypeSchema,
  siteCode: z.string().trim().optional().nullable(),
  siteId: z.string().uuid().optional().nullable(),
  hourlyCost: z.coerce.number().min(0).optional().nullable(),
  requiredCertification: certificationTypeSchema.optional().nullable(),
  status: equipmentStatusSchema.default("AVAILABLE"),
  notes: z.string().trim().optional().nullable(),
});

export const equipmentImportSchema = z.object({
  rows: z.array(equipmentImportRowSchema).min(1).max(500),
});

export const equipmentImportIssueSchema = z.object({
  message: z.string(),
});

export const equipmentImportPreviewRowSchema = z.object({
  row: z.number().int(),
  equipmentId: z.string(),
  name: z.string(),
  type: z.string().nullable(),
  siteCode: z.string().nullable(),
  hourlyCost: z.number().nullable(),
  requiredCertification: z.string().nullable(),
  status: z.string().nullable(),
  notes: z.string().nullable(),
  errors: z.array(equipmentImportIssueSchema),
  warnings: z.array(equipmentImportIssueSchema),
  canImport: z.boolean(),
  parsed: equipmentImportRowSchema.optional(),
});

export const equipmentImportPreviewSchema = z.object({
  rows: z.array(equipmentImportPreviewRowSchema),
  summary: z.object({
    total: z.number().int(),
    ready: z.number().int(),
    blocked: z.number().int(),
    warningCount: z.number().int(),
  }),
});

export const equipmentImportResultSchema = z.object({
  created: z.number().int(),
  skipped: z.number().int(),
  errors: z.array(
    z.object({
      row: z.number().int(),
      equipmentId: z.string(),
      message: z.string(),
    }),
  ),
});

export type EquipmentType = z.infer<typeof equipmentTypeSchema>;
export type EquipmentStatus = z.infer<typeof equipmentStatusSchema>;
export type CreateEquipmentInput = z.infer<typeof createEquipmentSchema>;
export type UpdateEquipmentInput = z.infer<typeof updateEquipmentSchema>;
export type EquipmentSummary = z.infer<typeof equipmentSummarySchema>;
export type EquipmentDetail = z.infer<typeof equipmentDetailSchema>;
export type EquipmentFormOptions = z.infer<typeof equipmentFormOptionsSchema>;
export type EquipmentImportRow = z.infer<typeof equipmentImportRowSchema>;
export type EquipmentImportPreview = z.infer<typeof equipmentImportPreviewSchema>;
export type EquipmentImportResult = z.infer<typeof equipmentImportResultSchema>;
